import { gql } from "@apollo/client";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import addDays from "date-fns/addDays";
import endOfMonth from "date-fns/endOfMonth";
import startOfMonth from "date-fns/startOfMonth";
import {
  PayerTransactionLogEntry,
  payerTransactionLogEntryFragment,
} from "common/graphql/fragments/payerTransactionLogEntryFragment";
import { OrderBy } from "common/graphql/queryString";

export const GET_PAYMENT_TRANSACTIONS = gql`
  query GetPaymentTransactions(
    $limit: Int = 1000
    $offset: Int = 0
    $orderBy: [financial_transaction_log_order_by!] = [{ transaction_ts: desc }]
    $where: financial_transaction_log_bool_exp!
    $aggregateWhere: financial_transaction_log_no_balance_bool_exp!
  ) {
    transactions: financial_transaction_log(
      where: { _and: [{ status: { _eq: "regular" } }, $where] }
      order_by: $orderBy
      limit: $limit
      offset: $offset
    ) {
      ...payerTransactionLogEntryFragment
    }
    aggregate: financial_transaction_log_no_balance_aggregate(
      where: { _and: [{ status: { _eq: "regular" } }, $aggregateWhere] }
    ) {
      aggregate {
        count
      }
    }
  }
  ${payerTransactionLogEntryFragment}
`;

export interface GetPaymentTransactionsData {
  transactions: PayerTransactionLogEntry[];
  aggregate: {
    aggregate: {
      count: number;
    };
  };
}

export interface PayerTransactionOrderBy {
  transaction_ts?: OrderBy;
  index?: OrderBy;
  price?: OrderBy;
}

export interface PayerTransactionsFilter {
  _and?: PayerTransactionsFilter[];
  _or?: PayerTransactionsFilter[];
  transaction_ts?: { _gte?: string; _lte?: string; _lt?: string };
  index?: { _eq: number };
  address_city?: { _eq?: string; _ilike?: string };
  license_plate_number?: { _eq?: string; _ilike?: string };
  license_plate_state_code?: { _eq?: string; _ilike?: string };
  gateway_transaction_type?: { _eq?: string; _in?: string[]; _nin?: string[]; _is_null?: boolean };
  transaction_type?: { _eq?: string; _in?: string[] };
  reference_gateway_transaction_type?: { _eq?: string };
}

/**
 * AUT-6162: the `financial.transaction_type` values the table labels "Usage Fee" -- an explicit
 * allowlist, so a new non-gateway type (e.g. `adjustment`, which also has a null
 * gateway_transaction_type) never silently becomes a usage fee. `trip_charge` rows are per-trip
 * permit charges (PPABUS) and are usage fees too. Shared by USAGE_FEE_FILTER, `isParkRow` and the
 * GET_PAYER_MONTHLY_CHARGES park aggregate.
 */
export const USAGE_FEE_TRANSACTION_TYPES = ["park", "trip_charge"] as const;

export const USAGE_FEE_FILTER: PayerTransactionsFilter = {
  transaction_type: { _in: [...USAGE_FEE_TRANSACTION_TYPES] },
};

export interface GetPaymentTransactionsVars {
  limit?: number;
  offset?: number;
  orderBy?: PayerTransactionOrderBy[];
  where: PayerTransactionsFilter;
  aggregateWhere: PayerTransactionsFilter;
}

/**
 * Builds the Hasura `where` for the payer transactions table from the selected
 * month and the DataGrid filter model. Used by both the table query and the
 * Monthly Charges aggregate so they always reflect the same rows.
 */
export const buildPaymentTransactionsWhere = (
  selectedDate: Date,
  filterModel?: GridFilterModel,
): PayerTransactionsFilter => {
  const since = startOfMonth(selectedDate);
  const until = endOfMonth(selectedDate);

  const dateRange: PayerTransactionsFilter = {
    transaction_ts: { _gte: since.toISOString(), _lte: until.toISOString() },
  };
  const filters: PayerTransactionsFilter[] = [];

  (filterModel?.items ?? []).forEach(({ columnField, operatorValue, value }) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    switch (columnField) {
      case "transactionIndex": {
        const n = Number(value);
        if (operatorValue === "equals" && !isNaN(n)) {
          filters.push({ index: { _eq: n } });
        }
        break;
      }
      case "date": {
        const day = new Date(value as string);
        if (operatorValue === "is") {
          filters.push({ transaction_ts: { _gte: day.toISOString(), _lt: addDays(day, 1).toISOString() } });
        } else if (operatorValue === "before") {
          filters.push({ transaction_ts: { _lt: day.toISOString() } });
        } else if (operatorValue === "after") {
          filters.push({ transaction_ts: { _gte: addDays(day, 1).toISOString() } });
        }
        break;
      }
      case "city":
        filters.push(
          operatorValue === "equals" ? { address_city: { _eq: value } } : { address_city: { _ilike: `%${value}%` } },
        );
        break;
      case "licensePlate": {
        const input = String(value).toUpperCase().trim();
        const spaceIndex = input.indexOf(" ");
        if (spaceIndex !== -1) {
          // "CA FLAME" → exact state + plate contains
          const state = input.slice(0, spaceIndex);
          const plate = input.slice(spaceIndex + 1);
          filters.push(
            operatorValue === "equals"
              ? { license_plate_state_code: { _eq: state }, license_plate_number: { _eq: plate } }
              : { license_plate_state_code: { _eq: state }, license_plate_number: { _ilike: `%${plate}%` } },
          );
        } else if (operatorValue === "equals") {
          // "FLAME" equals → exact plate match
          filters.push({ license_plate_number: { _eq: input } });
        } else {
          // "CA" or "FLAME" contains → search both fields since column displays "STATE PLATE"
          filters.push({
            _or: [
              { license_plate_state_code: { _ilike: `%${input}%` } },
              { license_plate_number: { _ilike: `%${input}%` } },
            ],
          });
        }
        break;
      }
      case "transactionType":
        if (value === "Usage Fee") {
          filters.push(USAGE_FEE_FILTER);
        } else if (value === "Adjustment") {
          filters.push({ transaction_type: { _eq: "adjustment" } });
        } else if (value === "Session Payment") {
          filters.push({ gateway_transaction_type: { _eq: "session_payment" } });
        } else if (value === "Wallet Load") {
          filters.push({ gateway_transaction_type: { _in: ["initial_wallet_load", "wallet_reload"] } });
        } else if (value === "Refund") {
          filters.push({ gateway_transaction_type: { _eq: "wallet_refund" } });
        }
        break;
    }
  });

  return { _and: [dateRange, ...filters] };
};
